'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { Checkbox } from '@/shared/components/ui/checkbox';
import type { MazeShareChannel } from '@/contexts/games/analytics/mazeAnalytics';
import { Printer, Download, FileImage, Loader2 } from 'lucide-react';
import { MazeState, PrintConfig, RadialMazeState, VoronoiMazeState } from '../types/maze-types';
import { MazePrinter } from '../helpers/mazePrinter';
import { VoronoiMazeRenderer } from '../helpers/voronoiMazeRenderer';
import { RadialMazeRenderer } from '../helpers/radialMazeRenderer';

/** The print dialog receives whichever renderer state is on screen. */
type AnyMazeState = MazeState | RadialMazeState | VoronoiMazeState;
const isVoronoi = (s: AnyMazeState): s is VoronoiMazeState => 'mazeData' in s && 'canvasSize' in s;
const isRadial = (s: AnyMazeState): s is RadialMazeState => 'rings' in s && 'sectorsPerRing' in s;

interface MazePrintPreviewProps {
  isOpen: boolean;
  mazeState: AnyMazeState;
  canvasRef: HTMLCanvasElement | null;
  onClose: () => void;
  /** Fleet analytics hook-up: fired when the maze actually leaves the app (print / file). */
  onShare?: (channel: MazeShareChannel) => void;
}

/**
 * Print preview modal for maze game
 * Mobile-first: preview on top, settings below, sticky action buttons
 */
export function MazePrintPreview({
  isOpen,
  mazeState,
  canvasRef,
  onClose,
  onShare,
}: MazePrintPreviewProps) {
  const t = useTranslations('games.maze.print');
  const tCommon = useTranslations('common');

  const [config, setConfig] = useState<PrintConfig>({
    paperSize: 'A4',
    orientation: 'portrait',
    includeZones: true,
    includePath: false,
    includeQRCode: true,
    margin: 10
  });

  const [previewDataUrl, setPreviewDataUrl] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  // Generate QR code once
  React.useEffect(() => {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    if (!currentUrl) return;
    import('qrcode').then((QRCode) =>
      QRCode.toDataURL(currentUrl, { width: 100, margin: 1 })
    ).then(setQrDataUrl).catch(() => {});
  }, []);

  // Generate preview from canvas snapshot — updates when config changes
  React.useEffect(() => {
    if (!canvasRef) return;
    try {
      // Canvas already has the maze rendered (with or without path depending on current state)
      // For "includePath" toggle, we use the canvas as-is (path is drawn during gameplay)
      setPreviewDataUrl(canvasRef.toDataURL('image/png'));
    } catch {
      setPreviewDataUrl('');
    }
  }, [canvasRef, config]);

  const handlePrint = async () => {
    if (!previewDataUrl) return;
    try {
      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      const qr = config.includeQRCode ? qrDataUrl : '';

      const qrHtml = qr
        ? `<div style="text-align:center;margin-top:16px;font-size:11px;color:#666">
            <img src="${qr}" style="width:80px;height:80px" />
            <div style="margin-top:4px">${currentUrl}</div>
          </div>`
        : '';

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const pageSize = config.orientation === 'landscape' ? `${config.paperSize} landscape` : config.paperSize;
        printWindow.document.write(`<!DOCTYPE html><html><head><title>Maze</title>
          <style>@media print{@page{size:${pageSize};margin:${config.margin}mm}body{margin:0;padding:0}} body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0} img.maze{max-width:100%;max-height:${qr ? '82vh' : '95vh'};object-fit:contain}</style>
          </head><body><img class="maze" src="${previewDataUrl}"/>${qrHtml}</body></html>`);
        printWindow.document.close();
        printWindow.onload = () => printWindow.print();
        onShare?.('print');
      }
    } catch (error) {
      console.error('Error printing maze:', error);
    }
  };

  const handleDownloadSVG = async () => {
    try {
      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      // Photo / shape (Voronoi) and radial mazes have their own vector exporters;
      // the grid printer only understands the rectangular cell grid.
      let svgString: string;
      if (isVoronoi(mazeState)) {
        svgString = VoronoiMazeRenderer.toSVG(mazeState, {
          width: mazeState.canvasSize.width,
          height: mazeState.canvasSize.height,
          includePath: config.includePath,
          includeZones: config.includeZones,
          isDarkMode: false,
        });
      } else if (isRadial(mazeState)) {
        const size = canvasRef ? Math.min(canvasRef.clientWidth || 800, canvasRef.clientHeight || 800) : 800;
        svgString = RadialMazeRenderer.toSVG(mazeState, {
          width: size,
          height: size,
          includePath: config.includePath,
          includeZones: config.includeZones,
          isDarkMode: false,
        });
      } else {
        svgString = await MazePrinter.canvasToSVG(mazeState, config, currentUrl);
      }
      const filename = `maze-${new Date().toISOString().slice(0, 10)}.svg`;
      MazePrinter.downloadAsSVG(svgString, filename);
      onShare?.('download_svg');
    } catch (error) {
      console.error('Error downloading SVG:', error);
    }
  };

  const handleDownloadPNG = async () => {
    if (!canvasRef) return;
    try {
      const filename = `maze-${new Date().toISOString().slice(0, 10)}.png`;
      await MazePrinter.downloadAsPNG(canvasRef, filename, 3);
      onShare?.('download_png');
    } catch (error) {
      console.error('Error downloading PNG:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-testid="maze-print-preview-dialog"
        className="max-w-4xl max-h-[90dvh] flex flex-col p-0 gap-0"
      >
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-6 sm:pt-6">
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2 sm:px-6">
          {/* Mobile: preview first, then settings. Desktop: side by side */}
          <div className="flex flex-col md:grid md:grid-cols-[1fr_auto] gap-4 md:gap-6">

            {/* Preview Panel — shown first on mobile */}
            <div className="space-y-2 order-1 md:order-2 md:w-[320px]">
              <Label className="hidden md:block">{t('preview')}</Label>
              <div className="border rounded-lg bg-muted/50 overflow-hidden">
                {previewDataUrl ? (
                  <div className={`p-2 sm:p-3 flex flex-col items-center justify-center gap-2 ${config.orientation === 'landscape' ? 'aspect-[1.414/1]' : 'aspect-[1/1.414]'}`}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={previewDataUrl}
                      alt="Maze preview"
                      className="w-full h-auto max-h-[35vh] object-contain"
                    />
                    {config.includeQRCode && qrDataUrl && (
                      <div className="flex flex-col items-center gap-1">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={qrDataUrl} alt="QR" className="w-12 h-12" />
                        <span className="text-[8px] text-muted-foreground truncate max-w-[200px]">
                          {typeof window !== 'undefined' ? window.location.href : ''}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 sm:h-64 text-muted-foreground gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {tCommon('loading')}
                  </div>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            <div className="space-y-4 order-2 md:order-1">
              {/* Paper size + Orientation in a compact row on mobile */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">{t('paperSize')}</Label>
                  <RadioGroup
                    value={config.paperSize}
                    onValueChange={(value) =>
                      setConfig({ ...config, paperSize: value as PrintConfig['paperSize'] })
                    }
                    className="space-y-1"
                  >
                    {(['A4', 'Letter', 'A3'] as const).map((size) => (
                      <div key={size} className="flex items-center space-x-2">
                        <RadioGroupItem value={size} id={size.toLowerCase()} />
                        <Label htmlFor={size.toLowerCase()} className="font-normal cursor-pointer text-xs sm:text-sm">
                          {size}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">{t('orientation')}</Label>
                  <RadioGroup
                    value={config.orientation}
                    onValueChange={(value) =>
                      setConfig({ ...config, orientation: value as PrintConfig['orientation'] })
                    }
                    className="space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="portrait" id="portrait" />
                      <Label htmlFor="portrait" className="font-normal cursor-pointer text-xs sm:text-sm">
                        {t('portrait')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="landscape" id="landscape" />
                      <Label htmlFor="landscape" className="font-normal cursor-pointer text-xs sm:text-sm">
                        {t('landscape')}
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Options as horizontal chips on mobile */}
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">{t('options')}</Label>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeZones"
                      checked={config.includeZones}
                      onCheckedChange={(checked) =>
                        setConfig({ ...config, includeZones: !!checked })
                      }
                    />
                    <Label htmlFor="includeZones" className="font-normal cursor-pointer text-xs sm:text-sm">
                      {t('includeZones')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includePath"
                      checked={config.includePath}
                      onCheckedChange={(checked) =>
                        setConfig({ ...config, includePath: !!checked })
                      }
                    />
                    <Label htmlFor="includePath" className="font-normal cursor-pointer text-xs sm:text-sm">
                      {t('includePath')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeQRCode"
                      checked={config.includeQRCode}
                      onCheckedChange={(checked) =>
                        setConfig({ ...config, includeQRCode: !!checked })
                      }
                    />
                    <Label htmlFor="includeQRCode" className="font-normal cursor-pointer text-xs sm:text-sm">
                      {t('includeQRCode')}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky action buttons */}
        <div className="border-t px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row gap-2">
          <Button data-testid="maze-print-now-button" type="button" onClick={handlePrint} className="flex-1 gap-2">
            <Printer className="size-4" />
            {t('printNow')}
          </Button>
          <Button data-testid="maze-download-svg-button" type="button" onClick={handleDownloadSVG} variant="outline" className="flex-1 gap-2">
            <Download className="size-4" />
            {t('downloadSVG')}
          </Button>
          <Button data-testid="maze-download-png-button" type="button" onClick={handleDownloadPNG} variant="outline" className="flex-1 gap-2">
            <FileImage className="size-4" />
            {t('downloadPNG')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
